import { IccPredefinedFillingLayout } from '@icc/shared/types/api/predefined-filling-layout';
import { Component, OnInit } from '@angular/core';
import { _, InfoService } from '@icc/shared/helpers';
import { ProfilesService } from '@icc/construction-elements/configurator';
import { StepComponent, ModalService } from '@icc/configurator/shared';
import { iccListItem, iccListTab } from '@icc/configurator/ui';
import { Subscription } from 'rxjs';
import { WindowFacade } from '../../+state/window-door.facade';
import { FillingsService } from '../../modules/glazings/fillings.service';
import { map } from 'rxjs/operators';
import { ModelService } from './model.service';
import { ConfigurationsService } from '@icc/configurator/shared';
import { EventBusService, TranslateService } from '@icc/shared';
import { DoorActiveConfiguration } from '@icc/window-door/types';
import { ActiveSash } from '@icc/window-door/types';
import { ModelInfoComponent } from '../model-info/model-info.component';
import { FillingsFacade } from '../../glazings/fillings.facade';
import { DoorPortalsService } from '../../door-portals/door-portals.service';
import { ProfilesModalService } from '../../profiles/profiles-modal.service';
import { SlatsService } from '../slats/slats.service';
import { SplitSashService } from '../../modules/layout/split-sash.service';
import { SashesLayoutService } from '../../modules/layout/sashes-layout.service';
import { SashTypesService } from '../../modules/layout/sash-types.service';
import { BrowserShapeService } from '../../modules/layout/shape.service';
import { LayoutOptionsPageComponent } from '../../layout-options-page/layout-options-page.component';
import { PanelGlazingsListPageComponent } from '../../panel-glazings-list-page/panel-glazings-list-page.component';
import { IccFilling } from '@icc/window-door/types';
import { ConstructionLimitationService } from '../../modules/dimensions/construction-limitation.service';
import { WarrantyService } from '../../+price';

type LayoutNode = {
    type: 'split' | 'field';
    direction?: 'vertical' | 'horizontal'; 
    proportion?: number; 
    children?: LayoutNode[];
    size?: number; 
    fillingType?: 'glass' | 'panel';
};

type SplitOverheads = {
    edgeLeft: number;
    edgeRight: number;
    edgeTop: number;
    edgeBottom: number;
    mullionVerticalAxisToGlazing: number;
    mullionHorizontalAxisToGlazing: number;
};

type LayoutFillingSelection = {
    panel: IccFilling | null;
    glazing: IccFilling | null;
};

@Component({
    selector: 'icc-model',
    templateUrl: './model.component.html',
    styleUrls: ['./model.component.scss'],
    standalone: false
})
export class ModelComponent extends StepComponent implements OnInit {
    static stepName = _('STEPS|Model');
    static stepIcon = {
        ligature: 'local_movies',
    };
    public configurator = 'door';
    public stepId = 'model';
    public title = _('DOOR|Model');
    
    sashActiveModels;
    public combinedModels: any[] = [];
    modelCategories$ = this.modelService.modelsGroups$.pipe(map(modelGroups => modelGroups.map<iccListTab>(group => ({
        id: Number(group.id),
        name: group.name,
    }))));
    currentModelGroup = null;
    selectedFilling$ = this.windowFacade.selectedModel$.pipe(
        map((filling) => filling && String(filling.id))
    );
    selectedModelForSashActive$ = this.windowFacade.selectedModelForSashActive$.pipe(
        map((filling) => filling && String(filling.id))
    )
    mode: 'list' | 'all' = 'list';
    conf: DoorActiveConfiguration;
    private subscriptions: Subscription[] = [];
    doorActiveModelBox: ReturnType<FillingsFacade['modelBox']>;
    doorActiveInnerModelBox: ReturnType<FillingsFacade['modelBox']>;
    doorPassiveModelBox: ReturnType<FillingsFacade['modelBox']>;
    doorPassiveInnerModelBox: ReturnType<FillingsFacade['modelBox']>;

    constructor(
        private windowFacade: WindowFacade,
        private modelService: ModelService,
        private fillingsService: FillingsService,
        private configurationsService: ConfigurationsService<'door'>,
        private modalService: ModalService,
        public fillingsFacade: FillingsFacade,
        private doorPortalsService: DoorPortalsService,
        private infoService: InfoService,
        private translateService: TranslateService,
        private profilesModalService: ProfilesModalService,
        private profilesService: ProfilesService,
        private slatsService: SlatsService,
        private splitSashService: SplitSashService,
        private sashesLayoutService: SashesLayoutService,
        private sashTypesService: SashTypesService,
        private shapeService: BrowserShapeService,
        private constructionLimitationService: ConstructionLimitationService,
        private warrantyService: WarrantyService,
        private eventBusService: EventBusService,
    ) {
        super();
    }

    ngOnInit() {
        this.conf = this.configurationsService.conf.Current;
        this.modelService.sashActiveModels$.subscribe(m => {
            this.sashActiveModels = m;
            this.updateCombinedModels();
        });
        this.subscriptions.push(
            this.fillingsFacade.doorActiveModelBox$.subscribe(box => {
                this.doorActiveModelBox = box;
            }),
            this.fillingsFacade.doorActiveInnerModelBox$.subscribe(box => {
                this.doorActiveInnerModelBox = box;
            }),
            this.fillingsFacade.doorPassiveModelBox$.subscribe(box => {
                this.doorPassiveModelBox = box;
            }),
            this.fillingsFacade.doorPassiveInnerModelBox$.subscribe(box => {
                this.doorPassiveInnerModelBox = box;
            }),
            this.windowFacade.modelHasBeenChosen$.subscribe(hasBeenChosen => {
                if (hasBeenChosen) {
                    this.mode = 'all';
                }
            })
        );
        this.subscriptions.push(
            this.windowFacade.width$.subscribe(() => {
                this.conf = this.configurationsService.conf.Current;
                this.updateCombinedModels();
            }),
            this.windowFacade.height$.subscribe(() => {
                this.conf = this.configurationsService.conf.Current;
                this.updateCombinedModels();
            }),
        );
        this.updateCombinedModels();
    }

    updateCombinedModels() {
        const panels = (this.sashActiveModels || []).map(panel => ({
            ...panel,
            type: 'panel',
            id: String(panel.id),
            title: panel.name,
            imageUrl: '/files/filling/' + panel.image,
            tabs: [panel.fillings_category_id],
            showInfo: true,
        }));
        let width = 0;
        let height = 0;
        const mainSash = this.fillingsFacade.getDoorSash(this.conf, 'doorActive');
        if (mainSash) {
            width = Number((mainSash as any).rWidth) || 0;
            height = Number((mainSash as any).rHeight) || 0;
        }
        const systemId = Number((this.conf as any)?.System?.id) || 0;
        const layouts = (this.modelService.predefinedFillingLayouts || [])
            .filter(layout => {
                if (layout.min_width && width && width < Number(layout.min_width)) return false;
                if (layout.max_width && width && width > Number(layout.max_width)) return false;
                if (layout.min_height && height && height < Number(layout.min_height)) return false;
                if (layout.max_height && height && height > Number(layout.max_height)) return false;

                if (
                    systemId
                    && Array.isArray(layout.unavailable_systems)
                    && layout.unavailable_systems.map(Number).includes(systemId)
                ) {
                    return false;
                }
                return true;
            })
            .map(layout => ({
                ...layout,
                type: 'layout',
                id: 'layout_' + layout.id,
                title: layout.name,
                imageUrl: layout.image ? '/' + layout.image : '',
                tabs: [layout.fillings_category_id],
                showInfo: true,
            }));
        this.combinedModels = [...layouts, ...panels];
    }

    changeOuterModel(field: string | undefined | ActiveSash) {
        if (field === 'doorActive') {
            this.mode = 'list';
            return;
        }
        if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            this.fillingsService.openModelModal(field, this.configurationsService.conf.Current, this.configurationsService.conf.Default);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    /**
     * Wybieranie modelu
     * @param  {Object} model Model
     */
    selectModel(modelItem: iccListItem) {
        if (modelItem.type === 'layout') {
            const layout = (this.modelService.predefinedFillingLayouts as IccPredefinedFillingLayout[]).find(
                l => String(l.id) === String(modelItem.id).replace('layout_', ''));
            if (!layout) {
                return;
            }
            const tree = this.parseLayoutRecursive(layout.code);

            const conf = this.configurationsService.conf.Current;
            const mainSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
            if (!mainSash) {
                return;
            }

            this.openLayoutOptionsModal(conf).then(selection => {
                if (!selection) return;
                this.modelService.beginLayoutApply({
                    id: layout.id,
                    name: layout.name || layout.name_cms || '',
                    image: layout.image || '',
                });
                try {
                    this.resetSashLayout(conf, mainSash);
                    if (DoorActiveConfiguration.is(conf)) {
                        conf.Model = null;
                        conf.ModelOptions = {} as typeof conf.ModelOptions;
                        conf.ModelOptions.hasBeenChosen = true;
                        conf.ModelOptions.predefinedLayoutId = layout.id;
                        conf.ModelOptions.predefinedLayoutName = layout.name || layout.name_cms || '';
                        conf.ModelOptions.predefinedLayoutImage = layout.image || '';
                    }
                    const overheads = this.getSplitOverheads(conf, mainSash);
                    const pauseId = this.eventBusService.pause([
                        'setGlazingInSash',
                        'setFillingColor',
                        'setPanelGlazing',
                        'setPanelTrim',
                    ]);
                    try {
                        this.applyLayoutTreeToSash(
                            this.splitSashService,
                            conf,
                            mainSash,
                            tree,
                            undefined,
                            overheads,
                            selection,
                        );
                    } finally {
                        this.eventBusService.resume(
                            ['setGlazingInSash', 'setFillingColor', 'setPanelGlazing', 'setPanelTrim'],
                            pauseId,
                        );
                    }
                    this.shapeService.setShapes(conf);
                    this.sashesLayoutService.setIndex(conf);
                    this.constructionLimitationService.findReinforcement(conf);
                    this.warrantyService.check(conf as any);
                    conf.Layout.changed = true;
                    if (typeof (this.profilesService as any).setDefaultMullions === 'function') {
                        (this.profilesService as any).setDefaultMullions(conf);
                    }
                    this.eventBusService.post({
                        key: 'changedSashes',
                        value: {},
                        conf,
                    });
                    this.eventBusService.post({
                        key: 'icc-redraw',
                        value: 'frame',
                        conf,
                    });
                    this.configurationsService.updatedConfiguration();
                } finally {
                    this.modelService.endLayoutApply();
                }
                this.mode = 'all';
            });
        } else if (modelItem.type === 'panel') {
            const model = this.sashActiveModels.find(m => String(m.id) === String(modelItem.id));
            if (model) {
                const conf = this.configurationsService.conf.Current;
                const mainSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
                if (mainSash && DoorActiveConfiguration.is(conf)) {
                    this.resetSashLayout(conf, mainSash);
                }
                this.modelService.selectModel(model, 'doorActive').then(() => {
                    if (this.doorActiveInnerModelBox.show || this.doorPassiveModelBox.show) {
                        this.mode = 'all';
                    }
                });
            }
        }
    }

    openModalDecoPanelOptions(field: string | undefined | ActiveSash) {
        if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            this.fillingsService.openModalPanelOptions(
                field,
                this.configurationsService.conf.Current
            );
        }
    }

    changePanelGlazing(field: string | undefined | ActiveSash) {
        if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            this.fillingsService.changePanelGlazing(
                field,
                this.configurationsService.conf.Current
            );
        }
    }

    changePanelTrim(field: string | undefined | ActiveSash) {
        if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            this.fillingsService.changePanelTrim(
                field,
                this.configurationsService.conf.Current
            );
        }
    }

    showInfo(item: iccListItem) {
        let model: any;
        if ((item as any)?.type === 'layout') {
            const rawId = String(item.id).replace(/^layout_/, '');
            model = (this.modelService.predefinedFillingLayouts || []).find(
                l => String(l.id) === rawId
            );
        } else {
            model = this.modelService.getModels().find(m => String(m.id) === String(item.id));
        }
        if (!model) {
            return;
        }
        const modalService = this.modalService
            .open({
                pageComponent: ModelInfoComponent,
                resolve: {
                    filling: () => model,
                    button: () => true,
                    imageUrl: () => item.imageUrl,
                },
            })
            .result.then((result: boolean) => {
                if (result) {
                    this.selectModel(item);
                }
            });
    }

    isActiveModelButton1Disabled(conf = this.configurationsService.conf.Current) {
        const fillings = this.fillingsService.getMatchingFillingsForDoorSashes(conf, "doorActive", true).filter(
            f => f.type === (conf.System.door_type ? 'door_panels' : 'deco_panels')
        );
        return fillings?.length <= 1;
    }

    isDoorActiveInnerButton1Disabled(conf = this.configurationsService.conf.Current) {
        const innerPanels = this.fillingsService.getPanelsBasedOnPanelBaseSide(conf, true);

        return innerPanels?.length <= 1;
    }

    isDoorPassiveInnerButton1Disabled(conf = this.configurationsService.conf.Current) {
        const innerPassivePanels = this.fillingsService.getPassiveInnerPanelsBasedOnPanelBaseSide(conf, true);

        return innerPassivePanels?.length <= 1;
    }

    isPassiveModelButton1Disabled(conf = this.configurationsService.conf.Current) {
        const fillings = this.fillingsService.getMatchingFillingsForDoorSashes(conf, 'doorPassive', true).filter(
            filling => filling.type === (conf.System.door_type ? 'door_panels' : 'deco_panels')
        );
        return fillings?.length <= 1;
    }

    isActiveModelButton2Disabled(field = 'doorActive', conf = this.configurationsService.conf.Current) {
        const fillingsData = this.fillingsService.getPanelGlazingsByFieldType(field, conf);
        const panelGlazings = fillingsData?.panelGlazings;
        return !this.doorActiveModelBox?.showOptions || panelGlazings?.length <= 1;
    }
    isPanelTrimsButtonDisabled(field = 'doorActive', conf = this.configurationsService.conf.Current) {
        const panelTrims = this.fillingsService.getPanelTrimsByFieldType(field, conf)?.panelTrims;

        if (field.includes('Inner')) {
            const outerPanelTrims = this.fillingsService.getPanelTrimsByFieldType(field.replace('Inner', ''), conf)?.panelTrims;
            const areTheSame = panelTrims?.length === outerPanelTrims?.length && panelTrims?.every((v) => outerPanelTrims.includes(v));
            if (areTheSame) {
                return true;
            }
        }

        if (panelTrims?.length <= 1) {
            return true;
        }
        return false;
    }

    changeSlat(conf = this.configurationsService.conf.Current) {
        if (DoorActiveConfiguration.is(conf)) {
            const activeSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
            this.slatsService.openModal(conf, activeSash);
        }
    }

    removeSlat(conf = this.configurationsService.conf.Current) {
        this.slatsService.removeSlat(conf);
    }

    sashHasSlat(conf = this.configurationsService.conf.Current) {
        return conf.Slat;
    }

    isSlatsAvailable(conf = this.configurationsService.conf.Current) {
        return this.slatsService.slatAvailable(conf)
    }

    parseLayoutRecursive(code: string): LayoutNode {
        code = code.trim();

        const split = this.findFirstTopLevelSplit(code, ['_', '|']);
        if (split) {
            const parts = this.splitAtTopLevel(code, split.operator);
            return {
                type: 'split',
                direction: split.operator === '_' ? 'horizontal' : 'vertical',
                children: parts.map(part => this.parseLayoutRecursive(part))
            };
        }

        const splitMatch = code.match(/^([0-9]+)?\((.+)\)$/);
        if (splitMatch) {
            const prop = splitMatch[1] ? parseInt(splitMatch[1], 10) : 1;
            const node = this.parseLayoutRecursive(splitMatch[2]);
            node.proportion = prop;
            return node;
        }

        const fieldMatch = code.match(/^([0-9]+)?mm?\(\[([PG])\]\)$/);
        if (fieldMatch) {
            return {
                type: 'field',
                size: fieldMatch[1] ? parseInt(fieldMatch[1], 10) : undefined,
                fillingType: fieldMatch[2] === 'P' ? 'panel' : 'glass',
            };
        }
        const simpleFieldMatch = code.match(/^\(\[([PG])\]\)$/) || code.match(/^\[([PG])\]$/);
        if (simpleFieldMatch) {
            return {
                type: 'field',
                size: undefined,
                fillingType: simpleFieldMatch[1] === 'P' ? 'panel' : 'glass',
            };
        }
        return { type: 'field' };
    }

    findFirstTopLevelSplit(code: string, operators: string[]): { operator: string, index: number } | null {
        let level = 0;
        for (let i = 0; i < code.length; i++) {
            if (code[i] === '(') level++;
            if (code[i] === ')') level--;
            if (level === 0 && operators.includes(code[i])) {
                return { operator: code[i], index: i };
            }
        }
        return null;
    }

    splitAtTopLevel(code: string, operator: string): string[] {
        let result = [];
        let level = 0;
        let last = 0;
        for (let i = 0; i < code.length; i++) {
            if (code[i] === '(') level++;
            if (code[i] === ')') level--;
            if (level === 0 && code[i] === operator) {
                result.push(code.slice(last, i));
                last = i + 1;
            }
        }
        result.push(code.slice(last));
        return result;
    }

private resetSashLayout(conf: DoorActiveConfiguration, sash: ActiveSash) {
        sash.intSashes = [];
        sash.intMullions = [];
        sash.intAlignments = [];
        sash.intEdgeSashes = { top: [], bottom: [], left: [], right: [] };
        this.sashTypesService.setType(sash, sash.type, conf);
    }

    applyLayoutTreeToSash(
        splitSashService: any,
        conf: DoorActiveConfiguration,
        sash: ActiveSash,
        node: LayoutNode,
        intSash?: ActiveSash,
        overheads?: SplitOverheads | null,
        selection?: LayoutFillingSelection,
    ) {
        if (!node || !sash) return;

        if (!intSash) {
            const rootIntSashes = sash.intSashes || [];
            if (rootIntSashes.length === 0) return;
            intSash = rootIntSashes[0];
        }

        if (overheads === undefined) {
            overheads = this.getSplitOverheads(conf, sash);
        }

        if (node.type === 'field') {
            if (node.fillingType && intSash) {
                this.applyFieldFilling(conf, sash, intSash, node.fillingType, selection);
            }
            return;
        }

        const children = node.children || [];
        if (children.length === 0) {
            return;
        }

        const splitSizes = this.getLayoutChildSizes(
            children,
            node.direction,
            intSash,
            overheads,
        );
        const splitSegments: ActiveSash[] = [intSash];

        if (children.length > 1) {
            let currentSegment: ActiveSash = intSash;
            for (let i = 0; i < children.length - 1; i++) {
                const newSegment = splitSashService.splitInternalSash(
                    sash,
                    currentSegment,
                    node.direction,
                    conf,
                    splitSizes[i],
                    true,
                );
                if (!newSegment) {
                    break;
                }
                splitSegments.push(newSegment);
                currentSegment = newSegment;
            }
        }

        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            const segment = splitSegments[i];
            if (!segment) continue;
            this.applyLayoutTreeToSash(
                splitSashService,
                conf,
                sash,
                child,
                segment,
                overheads,
                selection,
            );
        }
    }

    getSplitOverheads(conf: DoorActiveConfiguration, sash: ActiveSash): SplitOverheads | null {
        const drawData = (conf as any).drawData;
        const intSash = (sash.intSashes || [])[0];
        if (!drawData || !intSash) return null;

        const fillingData = drawData.filling?.find((f: any) => f.sashId === intSash.id);
        const glazingBeadData = drawData.glazingBead?.find((gb: any) => gb.sashId === intSash.id);
        const sashData = drawData.sash?.find((s: any) => s.sashId === sash.id);

        const fillingRect =
            (glazingBeadData?.inner?.rect && isFinite(glazingBeadData.inner.rect.x)
                ? glazingBeadData.inner.rect
                : fillingData?.rect) || null;
        const sashOuterRect = sashData?.outer?.rect || null;
        if (!fillingRect || !sashOuterRect) return null;

        const edgeLeft = Math.max(0, fillingRect.x - sashOuterRect.x);
        const edgeRight = Math.max(
            0,
            sashOuterRect.x + sashOuterRect.width - (fillingRect.x + fillingRect.width),
        );
        const edgeTop = Math.max(0, fillingRect.y - sashOuterRect.y);
        const edgeBottom = Math.max(
            0,
            sashOuterRect.y + sashOuterRect.height - (fillingRect.y + fillingRect.height),
        );

        const beadLeft = Math.max(
            0,
            (glazingBeadData?.outer?.rect && isFinite(glazingBeadData.outer.rect.x))
                ? fillingRect.x - glazingBeadData.outer.rect.x
                : 0,
        );
        const beadTop = Math.max(
            0,
            (glazingBeadData?.outer?.rect && isFinite(glazingBeadData.outer.rect.y))
                ? fillingRect.y - glazingBeadData.outer.rect.y
                : 0,
        );

        const vMullionProfileId = this.profilesService.getMullionProfileIdBetweenFields(
            conf,
            [intSash],
            [intSash],
            'vertical',
            true,
        );
        const hMullionProfileId = this.profilesService.getMullionProfileIdBetweenFields(
            conf,
            [intSash],
            [intSash],
            'horizontal',
            true,
        );
        const vMullionProfile = vMullionProfileId
            ? this.profilesService.getProfile(vMullionProfileId)
            : null;
        const hMullionProfile = hMullionProfileId
            ? this.profilesService.getProfile(hMullionProfileId)
            : null;

        const mullionVerticalAxisToGlazing =
            (vMullionProfile?.width || 0) / 2 + beadLeft;
        const mullionHorizontalAxisToGlazing =
            (hMullionProfile?.width || 0) / 2 + beadTop;

        return {
            edgeLeft,
            edgeRight,
            edgeTop,
            edgeBottom,
            mullionVerticalAxisToGlazing,
            mullionHorizontalAxisToGlazing,
        };
    }

    private applyFieldFilling(
        conf: DoorActiveConfiguration,
        sash: ActiveSash,
        intSash: ActiveSash,
        fillingType: 'glass' | 'panel',
        selection?: LayoutFillingSelection,
    ) {
        const picked =
            fillingType === 'panel' ? selection?.panel : selection?.glazing;
        if (!picked) return;
        this.fillingsService.setFillingInSash(intSash, sash, picked, conf, false, 'doorActive');
    }

    private openLayoutOptionsModal(
        conf: DoorActiveConfiguration,
    ): Promise<LayoutFillingSelection | undefined> {
        const matching = this.fillingsService.getMatchingFillingsForDoorSashes(
            conf,
            'doorActive',
            true,
        ) || [];

        const panelTypes = ['pvc_panels', 'panels_glazing'];
        const panels: IccFilling[] = matching.filter((f: any) => panelTypes.includes(f.type));
        const glazings: IccFilling[] = matching.filter((f: any) => f.type === 'glazing');

        const defaultGlassId = (conf.System as any)?.default_market_configuration?.glass_type_id
            ?? (conf.System as any)?.default_glass_type_id;
        let defaultGlazing: IccFilling | null = null;
        if (defaultGlassId) {
            defaultGlazing = glazings.find(
                f => Number(String(f.id).split('.')[0]) === Number(defaultGlassId),
            ) || null;
        }

        const sharedState: {
            panel: IccFilling | null;
            glazing: IccFilling | null;
        } = { panel: null, glazing: null };

        return Promise.resolve(
            this.modalService
                .open({
                    pageComponent: LayoutOptionsPageComponent,
                    resolve: {
                        panels: () => panels,
                        glazings: () => glazings,
                        defaultPanel: () => panels[0] || null,
                        defaultGlazing: () => defaultGlazing || glazings[0] || null,
                        state: () => sharedState,
                        pickFilling: () =>
                            (fillings: IccFilling[], selected: IccFilling | null) =>
                                Promise.resolve(
                                    this.modalService
                                        .open({
                                            pageComponent: PanelGlazingsListPageComponent,
                                            resolve: {
                                                fillings: () => fillings,
                                                selectedFilling: () => selected,
                                            },
                                        })
                                        .result,
                                ),
                    },
                })
                .result,
        ).then((res: any) => {
            if (!res) return undefined;
            return {
                panel: res.panel || null,
                glazing: res.glazing || null,
            } as LayoutFillingSelection;
        });
    }

    private getSideOverhead(
        intSash: ActiveSash,
        side: 'left' | 'right' | 'top' | 'bottom',
        overheads: SplitOverheads,
    ): number {
        const isEdge = intSash.nearMullions?.[side] === -1
            || intSash.nearMullions?.[side] == null;
        if (isEdge) {
            switch (side) {
                case 'left': return overheads.edgeLeft;
                case 'right': return overheads.edgeRight;
                case 'top': return overheads.edgeTop;
                case 'bottom': return overheads.edgeBottom;
            }
        }
        return side === 'left' || side === 'right'
            ? overheads.mullionVerticalAxisToGlazing
            : overheads.mullionHorizontalAxisToGlazing;
    }

    getLayoutChildSizes(
        children: LayoutNode[],
        direction: 'vertical' | 'horizontal',
        sash: ActiveSash,
        overheads?: SplitOverheads | null,
    ) {
        const availableSize = direction === 'horizontal' ? sash.rHeight : sash.rWidth;

        if (!overheads) {
            return this.distributeSizes(children, availableSize);
        }

        const startSide: 'left' | 'top' = direction === 'vertical' ? 'left' : 'top';
        const endSide: 'right' | 'bottom' = direction === 'vertical' ? 'right' : 'bottom';
        const startEdgeOverhead = this.getSideOverhead(sash, startSide, overheads);
        const endEdgeOverhead = this.getSideOverhead(sash, endSide, overheads);
        const mullionAxisToGlazing = direction === 'vertical'
            ? overheads.mullionVerticalAxisToGlazing
            : overheads.mullionHorizontalAxisToGlazing;

        const n = children.length;
        const fixedAxisSum = children.reduce((sum, c) => sum + (c.size || 0), 0);

        const glazingTotal = Math.max(
            0,
            availableSize
            - startEdgeOverhead
            - endEdgeOverhead
            - (n - 1) * 2 * mullionAxisToGlazing,
        );

        const childGlazingWidths: (number | null)[] = children.map((child, i) => {
            if (child.size == null) return null;
            const leftOh = i === 0 ? startEdgeOverhead : mullionAxisToGlazing;
            const rightOh = i === n - 1 ? endEdgeOverhead : mullionAxisToGlazing;
            return Math.max(0, child.size - leftOh - rightOh);
        });
        const fixedGlazingSum = childGlazingWidths.reduce<number>(
            (sum, w) => sum + (w || 0),
            0,
        );

        const weightedChildren = children.filter(c => c.size == null);
        const weightedTotal = weightedChildren.reduce(
            (sum, c) => sum + (c.proportion || 1),
            0,
        ) || 1;
        let remainingGlazing = Math.max(0, glazingTotal - fixedGlazingSum);
        let remainingWeight = weightedTotal;

        children.forEach((child, i) => {
            if (child.size != null) return;
            const weight = child.proportion || 1;
            const isLastWeighted = weightedChildren[weightedChildren.length - 1] === child;
            const w = isLastWeighted
                ? remainingGlazing
                : Math.floor((remainingGlazing * weight) / remainingWeight);
            childGlazingWidths[i] = w;
            remainingGlazing -= w;
            remainingWeight -= weight;
        });

        const axisSizes: number[] = children.map((child, i) => {
            if (child.size != null) return child.size;
            const leftOh = i === 0 ? startEdgeOverhead : mullionAxisToGlazing;
            const rightOh = i === n - 1 ? endEdgeOverhead : mullionAxisToGlazing;
            return Math.max(1, Math.round((childGlazingWidths[i] || 0) + leftOh + rightOh));
        });

        let sumExceptLast = 0;
        for (let i = 0; i < n - 1; i++) sumExceptLast += axisSizes[i];
        axisSizes[n - 1] = Math.max(1, availableSize - sumExceptLast);

        return axisSizes;
    }

    private distributeSizes(children: LayoutNode[], availableSize: number): number[] {
        const fixedSize = children.reduce((sum, child) => sum + (child.size || 0), 0);
        const weightedChildren = children.filter(child => child.size == null);
        const weightedTotal = weightedChildren.reduce(
            (sum, child) => sum + (child.proportion || 1),
            0,
        );
        let remainingSize = Math.max(0, availableSize - fixedSize);
        let remainingWeight = weightedTotal || 1;

        return children.map((child) => {
            if (child.size != null) return child.size;
            const weight = child.proportion || 1;
            const isLastWeighted = weightedChildren[weightedChildren.length - 1] === child;
            if (isLastWeighted) return remainingSize;
            const size = Math.floor((remainingSize * weight) / remainingWeight);
            remainingSize -= size;
            remainingWeight -= weight;
            return size;
        });
    }

    findLargestSash(sashes: any[], direction: 'vertical' | 'horizontal') {
        if (direction === 'vertical') {
            return sashes.reduce((max, s) => (!max || s.rWidth > max.rWidth ? s : max), null);
        } else {
            return sashes.reduce((max, s) => (!max || s.rHeight > max.rHeight ? s : max), null);
        }
    }

    getCurrentSashesAfterSplit(sash: any, conf: any, direction: 'vertical' | 'horizontal', n: number) {
        let sashes = sash.intSashes ? [...sash.intSashes] : [];
        if (!sashes.length && conf.Sashes) {
            sashes = conf.Sashes.filter(s => s.parentId === sash.id);
        }
        sashes.sort((a, b) => b.id - a.id);
        return sashes.slice(0, n);
    }

    sortSashesByDirection(sashes: any[], direction: 'vertical' | 'horizontal') {
        if (direction === 'vertical') {
            return [...sashes].sort((a, b) => a.rx - b.rx);
        } else {
            return [...sashes].sort((a, b) => a.ry - b.ry);
        }
    }
}
